<script lang="ts" setup>
  import { type PropType, computed } from 'vue';
  import { useHttpClient, useShared, useRoute } from '@othercommerce/foundation';
  import type { Breadcrumb } from '@storefront/types';
  import { useBreakpoints, useOffersPermissions, useSettlementsPermissions } from '@storefront/storefront';
  import { Features } from '@shared/features';

  defineProps({
    breadcrumbs: { type: Array as PropType<Breadcrumb[]>, required: true },
  });

  const route = useRoute();
  const breakpoints = useBreakpoints();
  const shared = useShared();

  const { canRequestOffers } = useOffersPermissions();
  const { canRequestSettlements } = useSettlementsPermissions();
  const isComplaintEnabled = computed(() => shared.value.isComplaintEnabled);
  const http = useHttpClient();

  function actionLogout() {
    http.delete(route('web.account.session.destroy'));
  }
</script>
<template>
  <main id="main" class="account">
    <template v-if="breakpoints.lg">
      <SfSkipToContentButton href="#account-menu">
        {{ $t('SHOP.ACCOUNT.ACTION.SKIP_TO_MENU') }}
      </SfSkipToContentButton>
      <SfSkipToContentButton href="#account-content">
        {{ $t('SHOP.ACCOUNT.ACTION.SKIP_TO_CONTENT') }}
      </SfSkipToContentButton>
    </template>
    <SfBreadcrumbs :breadcrumbs="breadcrumbs" />
    <div class="account__container">
      <nav v-if="breakpoints.lg" id="account-menu" :aria-label="$t('SHOP.ACCOUNT.ARIA.MENU')" class="account__menu">
        <ul>
          <li>
            <SfButton :href='$route("web.account.show")' explicit transparent>
              <template #before>
                <SfIconPanel aria-hidden="true" />
              </template>
              <template #default>
                {{ $t('SHOP.ACCOUNT.LABEL.MENU_ACCOUNT') }}
              </template>
            </SfButton>
          </li>
          <li>
            <SfButton :href="$route('web.account.order.index')" transparent>
              <template #before>
                <SfIconBox aria-hidden="true" />
              </template>
              <template #default>
                {{ $t('SHOP.ACCOUNT.LABEL.MENU_ORDERS') }}
              </template>
            </SfButton>
          </li>
          <template v-if="isComplaintEnabled">
            <li>
              <SfButton :href="$route('web.account.product.complaint.index')" transparent>
                <template #before>
                  <SfIconClipboardX aria-hidden="true" />
                </template>
                <template #default>
                  {{ $t('SHOP.ACCOUNT.LABEL.MENU_NEW_COMPLAINT') }}
                </template>
              </SfButton>
            </li>
            <li>
              <SfButton :href="$route('web.account.complaint.index')" transparent>
                <template #before>
                  <SfIconPersonX aria-hidden="true" />
                </template>
                <template #default>
                  {{ $t('SHOP.ACCOUNT.LABEL.MENU_COMPLAINTS') }}
                </template>
              </SfButton>
            </li>
            <li>
              <SfButton :href="$route('web.account.product.refund.index')" transparent>
                <template #before>
                  <SfIconArrowClockwise aria-hidden="true" />
                </template>
                <template #default>
                  {{ $t('SHOP.ACCOUNT.LABEL.MENU_NEW_REFUND') }}
                </template>
              </SfButton>
            </li>
            <li>
              <SfButton :href="$route('web.account.refund.index')" transparent>
                <template #before>
                  <SfIconPersonArrowClockwise aria-hidden="true" />
                </template>
                <template #default>
                  {{ $t('SHOP.ACCOUNT.LABEL.MENU_REFUNDS') }}
                </template>
              </SfButton>
            </li>
          </template>
          <li v-if="canRequestOffers">
            <SfButton :href="$route('web.account.offers.index')" transparent>
              <template #before>
                <SfIconEnvelopePaper aria-hidden="true" />
              </template>
              <template #default>
                {{ $t('SHOP.ACCOUNT.LABEL.MENU_OFFERS') }}
              </template>
            </SfButton>
          </li>
          <li v-if="canRequestSettlements">
            <SfButton :href="$route('web.account.settlements.index')" transparent>
              <template #before>
                <SfIconCoin aria-hidden="true" />
              </template>
              <template #default>
                {{ $t('SHOP.ACCOUNT.LABEL.MENU_SETTLEMENTS') }}
              </template>
            </SfButton>
          </li>
          <li v-if="canRequestSettlements">
            <SfButton :href="$route('web.account.warehouse.documents.index')" transparent>
              <template #before>
                <SfIconBox aria-hidden="true" />
              </template>
              <template #default>
                {{ $t('SHOP.ACCOUNT.LABEL.MENU_WAREHOUSE_DOCUMENTS') }}
              </template>
            </SfButton>
          </li>
          <li>
            <SfButton :href="$route('web.account.edit')" transparent>
              <template #before>
                <SfIconPersonGear aria-hidden="true" />
              </template>
              <template #default>
                {{ $t('SHOP.ACCOUNT.LABEL.MENU_ACCOUNT_DETAILS') }}
              </template>
            </SfButton>
          </li>
          <li>
            <SfButton :href="$route('web.account.notifications.edit')" transparent>
              <template #before>
                <SfIconBell aria-hidden="true" />
              </template>
              <template #default>
                {{ $t('SHOP.ACCOUNT.LABEL.MENU_NOTIFICATIONS') }}
              </template>
            </SfButton>
          </li>
          <li>
            <SfButton :href="$route('web.account.review.index')" transparent>
              <template #before>
                <SfIconChat aria-hidden="true" />
              </template>
              <template #default>
                {{ $t('SHOP.ACCOUNT.LABEL.MENU_REVIEWS') }}
              </template>
            </SfButton>
          </li>
          <li>
            <SfButton :href="$route('web.account.wishlist.index')" transparent>
              <template #before>
                <SfIconHeart aria-hidden="true" />
              </template>
              <template #default>
                {{ $t('SHOP.ACCOUNT.LABEL.MENU_WISHLISTS') }}
              </template>
            </SfButton>
          </li>
          <li>
            <SfButton :href="$route('web.account.address.shipping.index')" transparent>
              <template #before>
                <SfIconGeo aria-hidden="true" />
              </template>
              <template #default>
                {{ $t('SHOP.ACCOUNT.LABEL.MENU_SHIPPING_ADDRESSES') }}
              </template>
            </SfButton>
          </li>
          <li>
            <SfButton :href="$route('web.account.address.billing.index')" transparent>
              <template #before>
                <SfIconDocument aria-hidden="true" />
              </template>
              <template #default>
                {{ $t('SHOP.ACCOUNT.LABEL.MENU_BILLING_ADDRESSES') }}
              </template>
            </SfButton>
          </li>
          <li v-if="Features.loyaltyPoints.isEnabled">
            <SfButton :href="$route('web.account.loyalty.index')" transparent>
              <template #before>
                <SfIconCoin aria-hidden="true" />
              </template>
              <template #default>
                {{ $t('SHOP.ACCOUNT.LABEL.MENU_LOYALTY_POINTS') }}
              </template>
            </SfButton>
          </li>
          <li v-if="Features.affiliateMarketing.isEnabled">
            <SfButton :href="$route('web.account.affiliate.index')" transparent>
              <template #before>
                <SfIconShare aria-hidden="true" />
              </template>
              <template #default>
                {{ $t('SHOP.ACCOUNT.LABEL.MENU_AFFILIATE_PROGRAM') }}
              </template>
            </SfButton>
          </li>
          <li>
            <SfButton danger transparent @click="actionLogout">
              <template #before>
                <SfIconPower aria-hidden="true" />
              </template>
              <template #default>
                {{ $t('SHOP.ACCOUNT.LABEL.MENU_SIGN_OUT') }}
              </template>
            </SfButton>
          </li>
        </ul>
      </nav>
      <div id="account-content" class="account__content">
        <slot />
      </div>
    </div>
  </main>
</template>
<style lang="scss">
  @use "@storefront/styles/mixins/media";

  :root {
    --sf-account-menu-padding-x: 1.5rem;
    --sf-account-menu-padding-y: 1.5rem;

    --sf-account-menu-background: var(--section-background);
    --sf-account-menu-border-inline: var(--section-border-inline);
    --sf-account-menu-border-block: var(--section-border-block);
    --sf-account-menu-border-radius: var(--section-border-radius);

    --sf-account-color-white: rgb(255, 255, 255);
    --sf-account-color-black: rgb(0, 0, 0);
    --sf-account-color-dark-grey: rgb(80, 80, 80);
    --sf-account-color-medium-grey: rgb(220, 220, 220);
    --sf-account-color-light-background: rgb(240, 240, 240);

    --sf-account-loyalty-table-border-color: var(--sf-account-color-medium-grey);

    --sf-account-max-width: 1400px;
  }

  .account {
    margin: 0 auto 100px;
    width: 98%;
    max-width: var(--sf-account-max-width);

    &__heading {

      &.hide {
        display: none;
      }
    }

    &__container {
      margin-top: 1.875rem;
      display: grid;
      grid-template-columns: 1fr 4fr;
      align-items: flex-start;
      gap: 3rem;
    }

    &__menu {
      --sf-btn-justify-content: flex-start;

      background: var(--sf-account-menu-background);
      padding: var(--sf-account-menu-padding-x) var(--sf-account-menu-padding-y);
      border-radius: var(--sf-account-menu-border-radius);
      border-inline: var(--sf-account-menu-border-inline);
      border-block: var(--sf-account-menu-border-block);

      ul {
        display: flex;
        flex-direction: column;
        align-items: stretch;
        gap: 0.25rem;
        list-style: none;
      }

      svg {
        font-size: 1.25rem;
      }

      .btn {
        font-weight: var(--font-weight-semi);
        width: 100%;

        &.active {
          color: var(--sf-btn-transparent-foreground-active);
          background-color: var(--sf-btn-transparent-background-active);
        }
      }
    }

    &__content {
      min-width: 0;
      max-width: 100%;
    }
  }

  @include media.query(lg) {
    .account {
      margin-bottom: 50px;
      width: 100%;

      &__container {
        grid-template-columns: 1fr;
        margin: 0 auto 3.125rem;
      }
    }
  }
</style>
